import * as THREE from "three";
import { buildKeepDownOrientationTargetZFrame, quatToMj } from "../orientationTargets";
import type { IkKinematicsState } from "../types";
import type { RankedGraspWorldZ } from "./graspRanking";
import type { PickPlaceStage } from "./pickPlaceServoConfig";

export function isDropoffStage(stage: PickPlaceStage): boolean {
  return stage === "carry_hover" || stage === "drop_approach" || stage === "drop_open";
}

export function resolveBaseTargetPosZ(args: {
  stage: PickPlaceStage;
  graspAnchorZ: THREE.Vector3 | null;
  approachAnchorZ: THREE.Vector3 | null;
  targetPosZ: THREE.Vector3;
  activeTargetPosZ: THREE.Vector3;
}): THREE.Vector3 {
  const { stage, graspAnchorZ, approachAnchorZ, targetPosZ, activeTargetPosZ } = args;
  // IMPORTANT: once we close on the object, the object tends to move with the gripper.
  // If we keep targeting the *live* object pose during lift, we can create a feedback loop
  // where the target "moves up" as we lift, causing an endless climb.
  if (stage === "close" || stage === "lift") {
    return graspAnchorZ
      ? graspAnchorZ.clone()
      : approachAnchorZ
        ? approachAnchorZ.clone()
        : targetPosZ.clone();
  }
  if (stage === "approach" || stage === "final") {
    return approachAnchorZ ? approachAnchorZ.clone() : targetPosZ.clone();
  }
  return activeTargetPosZ.clone();
}

export function resolveDesiredZOffset(args: {
  stage: PickPlaceStage;
  dropoffBodyUuid: string | null;
  hoverHeight: number;
  approachHeight: number;
  finalHeight: number;
  liftDelta: number;
  carryHoverHeight: number;
  dropApproachHeight: number;
  carryLiftHeight: number;
}): number {
  const {
    stage,
    dropoffBodyUuid,
    hoverHeight,
    approachHeight,
    finalHeight,
    liftDelta,
    carryHoverHeight,
    dropApproachHeight,
    carryLiftHeight,
  } = args;
  return stage === "hover"
    ? hoverHeight
    : stage === "carry_hover"
      ? carryHoverHeight
      : stage === "approach"
        ? approachHeight
        : stage === "drop_approach" || stage === "drop_open"
          ? dropApproachHeight
          : stage === "final" || stage === "close"
            ? finalHeight
            : dropoffBodyUuid
              ? carryLiftHeight
              : approachHeight + liftDelta;
}

export function resolveTargetStageZ(args: {
  selectedGraspWorldZ: RankedGraspWorldZ | null;
  stage: PickPlaceStage;
  hoverHeight: number;
  approachHeight: number;
  graspFinalDownBias: number;
  baseTargetPosZ: THREE.Vector3;
  desiredZOffset: number;
}): THREE.Vector3 {
  const {
    selectedGraspWorldZ,
    stage,
    hoverHeight,
    approachHeight,
    graspFinalDownBias,
    baseTargetPosZ,
    desiredZOffset,
  } = args;
  // For grasp-driven pick stages, drive position from the selected grasp as early as hover
  // so the arm doesn't first converge toward object-center before switching to grasp target.
  // - hover: selected grasp + hover offset
  // - approach: selected grasp + approach offset
  // - final/close: selected grasp directly
  if (selectedGraspWorldZ) {
    if (stage === "hover") {
      return selectedGraspWorldZ.positionZ.clone().add(new THREE.Vector3(0, 0, hoverHeight));
    }
    if (stage === "final" || stage === "close") {
      return selectedGraspWorldZ.positionZ
        .clone()
        .add(new THREE.Vector3(0, 0, -graspFinalDownBias));
    }
    if (stage === "approach") {
      return selectedGraspWorldZ.positionZ.clone().add(new THREE.Vector3(0, 0, approachHeight));
    }
  }
  return baseTargetPosZ.clone().add(new THREE.Vector3(0, 0, desiredZOffset));
}

export function resolveDesiredRotationZ(args: {
  selectedGraspWorldZ: RankedGraspWorldZ | null;
  isPickStage: boolean;
  stage: PickPlaceStage;
  fkSeed: IkKinematicsState;
  gripperForwardLocal: THREE.Vector3;
  gripperUpLocal: THREE.Vector3;
}): {
  candidateDesiredRotZ: THREE.Quaternion;
  enforceGraspOrientation: boolean;
} {
  const { selectedGraspWorldZ, isPickStage, stage, fkSeed, gripperForwardLocal, gripperUpLocal } =
    args;
  const forwardForOrientation = new THREE.Vector3(0, 0, -1);
  // Choose a stable "up" reference to avoid arbitrary roll when forward is parallel to worldUp.
  const worldUpForRoll = new THREE.Vector3(0, 1, 0);
  // During hover, keep permissive top-down orientation so the arm can move into position.
  // Apply grasp-specific orientation from approach onward.
  const useGraspOrientationNow =
    !!selectedGraspWorldZ &&
    isPickStage &&
    (stage === "approach" || stage === "final" || stage === "close");
  const graspDesiredRotZ = useGraspOrientationNow ? selectedGraspWorldZ.orientationZ : null;

  return {
    enforceGraspOrientation: useGraspOrientationNow,
    candidateDesiredRotZ: graspDesiredRotZ
      ? graspDesiredRotZ
      : buildKeepDownOrientationTargetZFrame({
          currentRotationZ: fkSeed.endEffector.rotation.clone(),
          forwardLocal: gripperForwardLocal,
          upLocal: gripperUpLocal,
          forwardWorldZ: forwardForOrientation,
          worldUpZ: worldUpForRoll,
          symmetryTurns: 4,
        }),
  };
}

export function createIkTargets(args: {
  targetStageZ: THREE.Vector3;
  desiredRotZ: THREE.Quaternion;
}) {
  const { targetStageZ, desiredRotZ } = args;
  return {
    poseTarget: {
      position: [targetStageZ.x, targetStageZ.y, targetStageZ.z] as [number, number, number],
      orientation: quatToMj(desiredRotZ),
    },
    posTarget: {
      position: [targetStageZ.x, targetStageZ.y, targetStageZ.z] as [number, number, number],
    },
  };
}
